import { Card } from '@/components/ui/card'
import { Sparkles, Zap, Info, Calendar, Users, MessageCircle, Star } from 'lucide-react'
import { APP_CONSTANTS } from '@/lib/constants/app'

// Sports schedule data from Flutter app
const SPORTS_SCHEDULE = [
  { sport: 'Box Cricket (Men)', date: '4-5 Apr', age: '13+', width: 'w-40' },
  { sport: 'Box Cricket (Ladies)', date: '3-4 Apr', age: '7+', width: 'w-48' },
  { sport: 'Box Cricket (Legends)', date: '3-4 Apr', age: '50+', width: 'w-48' },
  { sport: 'Volleyball', date: '11-12 Apr', age: '15+', width: 'w-40' },
  { sport: 'Ladies Cricket', date: '11-12 Apr', age: '7+', width: 'w-40' },
  { sport: 'Kids Cricket', date: '11-12 Apr', age: '7-13', width: 'w-40' },
  { sport: 'Football', date: '18 Apr', age: '13+', width: 'w-40' },
  { sport: 'Legends Cricket', date: '18-19 Apr', age: '40+', width: 'w-40' },
  { sport: 'Teens Cricket', date: '18-19 Apr', age: '12-17', width: 'w-40' },
  { sport: 'Pickleball', date: '24-26 Apr', age: '13+', width: 'w-40' },
  { sport: "Men's Cricket", date: '1-3 May', age: '14+', width: 'w-40' },
]

export function EventInfoSection() {
  const openWhatsApp = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '')
    window.open(`https://wa.me/91${cleanPhone}`, '_blank')
  }

  return (
    <Card variant="premium" className="overflow-hidden">
      {/* Header with gradient */}
      <div className="relative bg-gradient-to-r from-primary via-primary/95 to-primary/90 px-6 py-5">
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">
              Welcome to YPL Season 11!
            </h2>
          </div>
        </div>
        {/* Decorative gradient orb */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Tournament Schedule - Horizontal Scroll */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-slate-900 text-sm">Tournament Schedule</h3>
          </div>

          {/* Horizontal scrolling container */}
          <div className="relative">
            <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent pr-4">
              {SPORTS_SCHEDULE.map((item, index) => (
                <div
                  key={item.sport}
                  className={`flex-shrink-0 ${item.width} p-4 rounded-xl bg-primary/5 border border-primary/20 hover:border-primary/40 transition-colors ${
                    index === SPORTS_SCHEDULE.length - 1 ? 'mr-4' : ''
                  }`}
                >
                  <div className="font-semibold text-slate-900 text-sm mb-2">
                    {item.sport}
                  </div>
                  <div className="flex items-center gap-1.5 text-primary text-xs font-medium mb-1">
                    <Calendar className="w-3 h-3" />
                    {item.date}
                  </div>
                  <div className="text-xs text-slate-500">
                    Ages: {item.age}
                  </div>
                </div>
              ))}
            </div>
            {/* Fade indicator on right */}
            <div className="absolute right-0 top-0 bottom-3 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          </div>

          {/* Age criteria note */}
          <p className="text-xs text-slate-500 mt-2 italic">
            * Age criteria must be met as of March 31, 2026
          </p>
        </div>

        {/* Registration Fees */}
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-sm mb-1">Registration Fees</h3>
              <p className="text-slate-600 text-sm">
                <span className="font-bold text-slate-900">₹1,200</span> for the first sport,{' '}
                <span className="font-bold text-slate-900">₹500</span> for every additional sport
              </p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm font-medium text-amber-900">
              Includes auction dinner and jerseys!
            </span>
          </div>
        </div>

        {/* Building Coordinators - Horizontal Scroll */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-slate-900 text-sm">Building Coordinators</h3>
          </div>

          {/* Horizontal scrolling container */}
          <div className="relative">
            <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent pr-4">
              {APP_CONSTANTS.COORDINATORS.map((coordinator, index) => (
                <button
                  key={coordinator.phone}
                  onClick={() => openWhatsApp(coordinator.phone)}
                  className={`flex-shrink-0 w-44 p-4 rounded-xl bg-white border border-slate-200 hover:border-green-500/50 hover:shadow-md transition-all text-left group ${
                    index === APP_CONSTANTS.COORDINATORS.length - 1 ? 'mr-2' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 text-sm truncate">
                        {coordinator.name}
                      </div>
                      <div className="text-xs text-slate-500">{coordinator.building}</div>
                      <div className="text-xs text-slate-400">{coordinator.phone}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {/* Fade indicator on right */}
            <div className="absolute right-0 top-0 bottom-3 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          </div>
        </div>

        {/* Info note */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-sm text-blue-800 leading-relaxed pt-1">
            Please ensure all information is accurate before submitting. Your registration
            will be confirmed after payment verification. Contact your building coordinator
            for any assistance.
          </p>
        </div>
      </div>
    </Card>
  )
}
